import { HourlyForecastResponse } from '../services/HourlyFogForecastService';
import { HourlyForecastTimelineData, HourlyForecastTimelineSegment, ErrorInfo } from '../models/HourlyForecastTimelineData';
import { NZAirports } from '../types/Locations';
import { ALL_AIRPORTS_DATA } from '../data/locations';
import { parseOperatingHours } from './operatingHours';

// Extended timeline data interface
interface ExtendedHourlyTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
}

// Toggle for using 30-minute actuals instead of 60-minute actuals
// TODO: Make this configurable via UI toggle
const USE_HALF_HOURLY_ACTUALS = true;

/**
 * Maps the raw hourly forecast data into a timeline-friendly format
 * @param forecast The raw hourly forecast data from the API
 * @param locationId The location ID
 * @param locationName The location name/code
 * @returns A timeline-friendly data structure
 */
export function mapHourlyForecastToTimelineData(
  forecast: HourlyForecastResponse,
  locationId: number,
  locationName: string
): ExtendedHourlyTimelineData {
  const timelineSegments: HourlyForecastTimelineSegment[] = [];

  // Select actuals source based on toggle (30-min or 60-min buckets)
  const actualsSource = USE_HALF_HOURLY_ACTUALS
    ? forecast.half_hourly_actuals
    : forecast.aggregated_actuals;

  // Process actuals - these are already in the correct time segments
  // Use visibility_hmean for consistency with fog_predicted calculation
  actualsSource.forEach(actual => {
    timelineSegments.push({
      time: new Date(actual.bucket_start),
      fog_predicted: actual.fog_predicted,
      visibility_level: actual.visibility_hmean,
      lowest_visibility_level: actual.lowest_visibility_level ?? null,
      highest_visibility_level: actual.highest_visibility_level ?? null,
      raw_actuals: actual,
      raw_predictions: null,
      visibility_mean: actual.visibility_mean
    });
  });

  // Process predictions - these are already in the correct time segments (always 60-min)
  forecast.hourly_buckets.forEach(prediction => {
    timelineSegments.push({
      time: new Date(prediction.bucket_start),
      fog_predicted: prediction.fog_predicted,
      visibility_level: prediction.lowest_visibility_level,
      lowest_visibility_level: null,
      highest_visibility_level: null,
      raw_actuals: null,
      raw_predictions: prediction,
      visibility_mean: null
    });
  });

  // Ensure segments are sorted by time
  const sortedSegments = timelineSegments.sort(
    (a, b) => a.time.getTime() - b.time.getTime()
  );

  // Create the timeline data with errors from the forecast
  // Segment interval depends on actuals toggle (30-min or 60-min)
  const result: ExtendedHourlyTimelineData = {
    location_id: locationId,
    location_name: locationName,
    segments: sortedSegments,
    segment_interval: USE_HALF_HOURLY_ACTUALS ? 30 : 60
  };

  // Include errors if present in the forecast
  if (forecast.errors && forecast.errors.length > 0) {
    console.log(`Including ${forecast.errors.length} top-level errors from hourly forecast for ${locationName}:`, forecast.errors);
    result.errors = forecast.errors;

    // Extract operating hours from OUT_OF_OPERATING_HOURS error
    const outOfHoursError = forecast.errors.find(e => e.code === 'OUT_OF_OPERATING_HOURS');
    if (outOfHoursError?.details?.operating_hours) {
      result.operatingHours = parseOperatingHours(outOfHoursError.details.operating_hours as string) ?? undefined;
    }

    // Log the mapped timeline data when errors are present
    console.log(`Mapped hourly timeline data for ${locationName} (with errors):`, result);
  }

  return result;
}

/**
 * Gets the airport name for a location ID
 * @param locationId The location ID
 * @returns The airport name (e.g., "Auckland") or ICAO code as fallback
 */
export function getAirportName(locationId: number): string {
  const airport = ALL_AIRPORTS_DATA.find(a => a.id === locationId);
  if (airport) {
    return airport.name;
  }
  // Fallback to ICAO code if not found in data
  return NZAirports[locationId] || `Unknown (${locationId})`;
}

/**
 * Maps hourly forecasts for multiple locations into timeline data
 */
export function mapMultipleHourlyForecasts(
  forecasts: Array<{ locationId: number, forecast: HourlyForecastResponse }>
): ExtendedHourlyTimelineData[] {
  return forecasts.map(item => {
    const locationId = item.locationId;
    const locationName = getAirportName(locationId);

    return mapHourlyForecastToTimelineData(
      item.forecast,
      locationId,
      locationName
    );
  });
}
